import { useDepositWizard } from "features/wallet/components/deposit/hooks/useDepositWizard";
import { useCompleteDeposit } from "features/wallet/components/deposit/hooks/useCompleteDeposit";
import React, { useEffect, useState } from "react";
import {
  DepositContent,
  DepositContentSkeleton,
} from "../../blocks/deposit-content/DepositContent";
import { DepositAmountInput } from "../../blocks/deposit-amount-input/DepositAmountInput";
import { DepositMethodField } from "../../blocks/deposit-method-field/DepositMethodField";
import { SectionHeader } from "../../blocks/section-header/SectionHeader";
import { GooglePayButton } from "features/wallet/components/GooglePayButton";
import { useWalletStore } from "features/wallet/stores/walletStore";
import { useDepositAmountValidationSchema } from "features/wallet/hooks/useDepositAmountValidationSchema";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type z from "zod";
import * as styles from "./GooglePayPrimaryView.module.scss";
import { Form, Modal, Skeleton, toast } from "@picklebet/ui";
import { SectionDivider } from "../../blocks/section-divider/SectionDivider";
import { AmountInputSkeleton } from "@/features/wallet/components/shared/amount-input-skeleton/AmountInputSkeleton";
import { MethodRowSkeleton } from "../../method-row/MethodRow";
import { useReportSubmitting } from "features/wallet/hooks/useReportSubmitting";

export const GooglePayPrimaryViewSkeleton = () => {
  return (
    <DepositContentSkeleton>
      <div className={styles.fields}>
        <AmountInputSkeleton />

        <SectionDivider />

        <SectionHeader>Payment Method</SectionHeader>

        <MethodRowSkeleton />
      </div>

      <Modal.Footer>
        <Skeleton className={styles.buttonSkeleton} />
      </Modal.Footer>
    </DepositContentSkeleton>
  );
};

export const GooglePayPrimaryView = () => {
  const googleDepositStatus = useWalletStore(
    (state) => state.googleDepositStatus,
  );
  const googleDepositError = useWalletStore(
    (state) => state.googleDepositError,
  );
  const setGoogleDepositError = useWalletStore(
    (state) => state.setGoogleDepositError,
  );
  const setGoogleDepositStatus = useWalletStore(
    (state) => state.setGoogleDepositStatus,
  );

  const { amount, setAmount, setSubmitting } = useDepositWizard();
  const completeDeposit = useCompleteDeposit();
  const [schema, loading] = useDepositAmountValidationSchema();

  const isProcessing = googleDepositStatus === "processing";

  useReportSubmitting(isProcessing, setSubmitting);

  useEffect(() => {
    if (googleDepositStatus === "success") {
      completeDeposit();
    }
  }, [googleDepositStatus]);

  useEffect(() => () => setGoogleDepositStatus("ready"), []);

  const {
    control,
    formState: { errors, isValid },
    watch,
    trigger,
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      amount,
    },
  });

  const amountFormValue = watch("amount");
  const [amountErrorShown, setAmountErrorShown] = useState(false);

  // this form only validates when clicking the Google Pay button, trigger
  // validation when the amount changes instead
  useEffect(() => {
    if (amountErrorShown) {
      trigger();
    }
  }, [amountFormValue, amountErrorShown]);

  // consumed as soon as it is raised, so a retry can surface the same message
  useEffect(() => {
    if (!googleDepositError) return;

    toast({
      variant: "danger",
      title: "Deposit Failed",
      description: googleDepositError,
    });

    setGoogleDepositError(null);
  }, [googleDepositError]);

  return (
    <Form noSpacing>
      <DepositContent>
        <div className={styles.fields} data-locked={isProcessing}>
          <Controller
            control={control}
            name="amount"
            render={({ field: { onChange, value, ...field } }) => (
              <DepositAmountInput
                value={value}
                onChange={(next) => {
                  onChange(next);
                  setAmount(next);
                }}
                errorMessage={errors.amount?.message}
                {...field}
              />
            )}
          />

          <SectionDivider />

          <SectionHeader>Payment Method</SectionHeader>

          <DepositMethodField />
        </div>

        <Modal.Footer>
          <GooglePayButton
            className={styles.googlePayButtonContainer}
            loading={loading}
            processing={isProcessing}
            isFinal={isValid}
            isValid={isValid}
            amount={amountFormValue}
            onValidationError={() => setAmountErrorShown(true)}
          />
        </Modal.Footer>
      </DepositContent>
    </Form>
  );
};
